import { DEBOUNCE } from '@/core/core.constants';
import { sqInvestigateStore, sqManualSignalStore } from '@/core/core.stores';
import { PUSH_IGNORE } from '@/core/flux.service';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { generateFormula } from '@/tools/manualSignal/manualSignal.utilities';
import { cancelPreviewSeries, generatePreviewSeries, removePreviewSeries } from '@/trendData/trend.actions';
import { PREVIEW_ID } from '@/trendData/trendData.constants';
import { asyncDebounce } from '@/utilities/asyncDebounce';
import { logError } from '@/utilities/logger';

/**
 * Previews the signal being edited. The preview does not push a workstep: the store change that triggered it pushes
 * its own, and a second push once the preview resolves would make each change take two undos.
 */
export function generateManualSignalPreview(): Promise<unknown> {
  removePreviewSeries(PUSH_IGNORE);
  const { interpolationMethod, maximumInterpolation, samples, uom, id, color } = sqManualSignalStore.getState();
  if (sqInvestigateStore.activeTool === TREND_TOOLS.MANUAL_SIGNAL && samples.length > 0) {
    return generatePreviewSeries(
      generateFormula({ interpolationMethod, maximumInterpolation, samples, uom }),
      {},
      id ? id : PREVIEW_ID,
      color,
      PUSH_IGNORE,
    ).catch(() => removePreviewSeries(PUSH_IGNORE));
  } else {
    void cancelPreviewSeries();
    return Promise.resolve();
  }
}

const generateManualSignalPreviewDebounced = asyncDebounce(generateManualSignalPreview, DEBOUNCE.PREVIEW);

/**
 * Schedules a preview of the signal being edited. Module-level so that every component editing the Manual Signal
 * store shares one debounce, and so navigation can clear it via clearOutstandingDebouncedAsyncFunctions.
 */
export function triggerManualSignalPreview(): void {
  generateManualSignalPreviewDebounced().catch((error: unknown) => {
    // asyncDebounce rejects superseded calls with undefined; that is expected, not an error
    if (error !== undefined) logError(error instanceof Error ? error : String(error));
  });
}
